import { Ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';

/**
 * Composable to synchronize dropdown state across multiple dropdowns.
 * When one dropdown opens, all others close automatically.
 *
 * @param isOpen - A reactive ref controlling the open/closed state of the dropdown
 */
export function useDropdownSync(isOpen: Ref<boolean>) {
  const instance = getCurrentInstance();
  const proxy = instance?.proxy;
  const root = proxy?.$root;

  const eventEmitter = root?.$global_eventEmitter || proxy?.$global_eventEmitter;

  if (!eventEmitter) {
    console.warn('useDropdownSync: $global_eventEmitter is not available on root or proxy');
    return;
  }

  // Listen for modal opens and other dropdown opens
  onMounted(() => {
    // Close dropdown when a modal opens
    eventEmitter.$on('modalOpen', () => {
      isOpen.value = false;
    });

    // Close dropdown when another dropdown opens
    eventEmitter.$on('dropdown-opened', (uid: number) => {
      // If another dropdown opened, close this one
      if (uid !== proxy._uid && isOpen.value) {
        isOpen.value = false;
      }
    });
  });

  // Emit event when this dropdown opens
  watch(isOpen, (isNowOpen) => {
    if (isNowOpen) {
      eventEmitter.$emit('dropdown-opened', proxy._uid);
    }
  });

  // Clean up event listeners
  onBeforeUnmount(() => {
    eventEmitter.$off('modalOpen');
    eventEmitter.$off('dropdown-opened');
  });
}
